import { ItemListPosition, Logger, NotificationService, useIntl } from '@capsicum/common';
import {
	ActivityIndicator,
	Button,
	Goat,
	InfoCard,
	NetworkFeeCard,
	SmartContractAddress,
	Snackbar,
	Text,
	useRouter
} from '@capsicum/common-web';
import { ApprovedFooter, HeaderWarning } from '@capsicum/features/regulations/web';
import { usePostSurveyVoteBySurveyId, useSurveyVoteScreen } from '@capsicum/features/survey/shared';
import { useEffect } from 'react';
import styled from 'styled-components';

import { useSurveyVoteTransaction } from '../../hooks/useSurveyVoteTransaction';

export type SurveyVoteProps = {
	answerId: string;
	partnerId: string;
	surveyId: string;
};

export const SurveyVote = ({ answerId, partnerId, surveyId }: SurveyVoteProps) => {
	const { $t } = useIntl();
	const { replace } = useRouter();
	const { isVoteError, isVoteProcessing, isVoteSuccessful, submitVote } = usePostSurveyVoteBySurveyId(
		partnerId,
		surveyId
	);

	const {
		closeAt,
		currencyDecimalDetail,
		currencyShortName,
		intlConfirm,
		// TRANSLATIONS
		intlTitle,
		isError,
		// GET request(s)
		isLoading,
		partnerLogo,
		selectedAnswerId,
		selectedAnswerText,
		stakingContractAddress,
		surveyContractAddress,
		surveyQuestion,
		tokenContractAddress
	} = useSurveyVoteScreen({
		answerId,
		surveyId
	});

	/**
	 * Handles a failed transaction.
	 */
	const onTransactionFailure = () => {
		NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('survey.toast.error.topText')}
					testID="surveyDetails-error-notification"
					variant="error"
				/>
			)
		});
	};

	/**
	 * Handles an erroneous transaction.
	 */
	const onTransactionError = (error?: unknown) => {
		Logger.error('onTransactionError', {
			...(typeof error === 'object' ? error : {})
		});

		NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('survey.toast.error.topText')}
					testID="surveyDetails-error-notification"
					variant="error"
				/>
			)
		});
	};

	/**
	 * Handle the success of the transaction.
	 */
	const onTransactionSuccess = async (txHash?: string) => {
		try {
			// Call the updated SurveyVote POST endpoint
			await submitVote({ answerId, blockchainAddress: txHash, surveyId });
		} catch (error) {
			Logger.error('onSurveyVoteAPIerror', {
				...(typeof error === 'object' ? error : {})
			});

			NotificationService.addNotification({
				component: (
					<Snackbar
						description={$t('survey.toast.error.bottomText')}
						testID="surveyVote-error-notification"
						title={$t('survey.toast.error.topText')}
						variant="error"
					/>
				)
			});
		}
	};

	const { gasFeeEstimate, gasFeeEstimateVariant, isButtonDisabled, isTransactionLoading, sendVoteTransaction } =
		useSurveyVoteTransaction({
			answerId: selectedAnswerId || '',
			currencyDecimalDetail,
			currencyShortName: currencyShortName,
			onTransactionError,
			onTransactionFailure,
			onTransactionSuccess,
			stakeAmount: '1',
			stakingContractAddress: stakingContractAddress,
			surveyContractAddress: surveyContractAddress,
			tokenContractAddress: tokenContractAddress
		});

	const isSurveyVoteError = isError || isVoteError;
	const isTransactionAndVoteLoading = isTransactionLoading || isVoteProcessing;

	useEffect(() => {
		if (isVoteSuccessful) {
			replace(`/votes/${partnerId}/${surveyId}`);
		}
	}, [isVoteSuccessful, replace, partnerId, surveyId]);

	if (isLoading) {
		return (
			<S.ActivityIndicatorContainer>
				<ActivityIndicator />
			</S.ActivityIndicatorContainer>
		);
	}

	if (isSurveyVoteError) {
		return (
			<Goat
				image="ankward"
				testID="surveyDetails-confirmVote-errorGoat-image"
				title={$t('survey.details.error')}
			/>
		);
	}

	return (
		<S.Wrapper>
			<S.SurveyVoteHeaderWaring page="poll" />
			<S.Title variant="title5">{intlTitle}</S.Title>
			<S.ComponentLine>
				<InfoCard
					icon={partnerLogo}
					itemListPosition={ItemListPosition.START}
					// We need to hardcode to 1 for mvp
					mainRightText={`1 ${currencyShortName}`}
					showDivider
					testID="surveyDetails-confirmVote-lock"
					title={$t('survey.vote.lock')}
					tooltipText={$t('survey.vote.lockTooltip.description')}
					tooltipTitle={$t('survey.vote.lockTooltip.title')}
				/>
				<InfoCard
					itemListPosition={ItemListPosition.END}
					mainRightText={closeAt || ''}
					testID="surveyDetails-confirmVote-lockedUntil"
					title={$t('survey.vote.lockedUntil')}
				/>
			</S.ComponentLine>
			<S.ComponentLine>
				<NetworkFeeCard
					feeAmount={gasFeeEstimate}
					testID="surveyDetails-confirmVote-networkFee"
					variant={gasFeeEstimateVariant}
				/>
			</S.ComponentLine>
			<S.ComponentLine>
				<InfoCard
					itemListPosition={ItemListPosition.START}
					mainRightText={surveyQuestion || ''}
					showDivider
					testID="surveyDetails-confirmVote-question"
					title={$t('survey.vote.question')}
				/>
				<InfoCard
					itemListPosition={ItemListPosition.END}
					mainRightText={selectedAnswerText || ''}
					testID="surveyDetails-confirmVote-answer"
					title={$t('survey.vote.answer')}
				/>
			</S.ComponentLine>
			<S.ComponentLine>
				<SmartContractAddress
					contractAddress={stakingContractAddress || ''}
					path="address"
					testID="surveyDetails-confirmApprove-stakingContractAddress"
					variant="link"
				/>
			</S.ComponentLine>

			<S.ButtonWrapper>
				<S.StyledButton
					disabled={isButtonDisabled}
					isLoading={isTransactionAndVoteLoading}
					testID="surveyApprove-confirmVote-button"
					text={intlConfirm}
					onClick={sendVoteTransaction}
				/>
			</S.ButtonWrapper>
			<ApprovedFooter page="poll" />
		</S.Wrapper>
	);
};

export namespace S {
	export const ActivityIndicatorContainer = styled.div`
		display: flex;
		width: 100%;
		height: 100%;
		justify-content: center;
		padding-top: ${({ theme }) => theme.spacings.xxxlarge};
	`;

	export const Wrapper = styled.div`
		flex: 1;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		justify-content: space-between;
		padding: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const Title = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ComponentLine = styled.div`
		margin-top: ${({ theme }) => theme.spacings.medium};
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		justify-content: center;
	`;

	export const StyledButton = styled(Button)`
		margin-top: ${({ theme }) => theme.spacings.medium};
		${({ theme }) => theme.border.radius.circle};
		align-self: center;
	`;

	export const SurveyVoteHeaderWaring = styled(HeaderWarning)`
		margin-bottom: ${({ theme }) => theme.spacings.medium};
	`;
}
